"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

interface TransformOptions {
  prefix: string;
  suffix: string;
  skipEmpty: boolean;
  numbered: boolean;
}

function transformLine(
  line: string,
  index: number,
  opts: TransformOptions
): string {
  const numberPrefix = opts.numbered ? `${index}. ` : "";
  return `${numberPrefix}${opts.prefix}${line}${opts.suffix}`;
}

function applyPrefixSuffix(text: string, opts: TransformOptions): string {
  const lines = text.split("\n");
  const result: string[] = [];
  let counter = 0;
  for (const line of lines) {
    if (opts.skipEmpty && line.trim().length === 0) {
      result.push(line);
    } else {
      counter += 1;
      result.push(transformLine(line, counter, opts));
    }
  }
  return result.join("\n");
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const ADD_PREFIX_SUFFIX_STRINGS = {
  inputOneItemPerLine: "Input — one item per line",
  appleBananaCherry: "apple\nbanana\ncherry",
  line: "one: line|other: lines",
  prefixAddedToStartOf: "Prefix (added to start of each line)",
  suffixAddedToEndOf: "Suffix (added to end of each line)",
  skipEmptyLines: "Skip empty lines",
  numberLines123: "Number lines (1., 2., 3., …)",
  presets: "Presets",
  wrapInDoubleQuotes: 'Wrap in "double quotes"',
  addCommaSuffix: "Add comma suffix",
  markdownList: "Markdown list (- )",
  numberLines: "Number lines",
  apply: "Apply",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  formattedOutput: "Formatted output",
  inputLines: "Input lines",
  outputLines: "Output lines",
  outputCharacters: "Output characters",
  prefixPlaceholder: 'e.g. "- " or "> "',
  suffixPlaceholder: 'e.g. "," or ";"',
} as const;

export default function AddPrefixSuffixWidget() {
  const [input, setInput] = useState("");
  const [prefix, setPrefix] = useState("");
  const [suffix, setSuffix] = useState("");
  const [skipEmpty, setSkipEmpty] = useState(true);
  const [numbered, setNumbered] = useState(false);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const lineCount = input.trim().length === 0 ? 0 : input.split("\n").length;
  const outputLineCount =
    output.trim().length === 0 ? 0 : output.split("\n").length;

  function handleApply() {
    if (input.length === 0) {
      return;
    }
    setOutput(
      applyPrefixSuffix(input, { prefix, suffix, skipEmpty, numbered })
    );
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setCopied(false);
  }

  function presetQuotes() {
    setPrefix('"');
    setSuffix('"');
    setNumbered(false);
  }

  function presetCommaSuffix() {
    setSuffix(",");
    setNumbered(false);
  }

  function presetMarkdownList() {
    setPrefix("- ");
    setSuffix("");
    setNumbered(false);
  }

  function presetNumberLines() {
    setPrefix("");
    setSuffix("");
    setNumbered(true);
  }

  const locale = useToolLocale();
  const strings = useToolUi(ADD_PREFIX_SUFFIX_STRINGS);
  const fmt = useToolNumber();

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="prefix-input"
        >
          {strings.inputOneItemPerLine}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="prefix-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.appleBananaCherry}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {fmt(lineCount)}
          {tPlural(strings.line, locale, lineCount)}
        </p>
      </div>
      {/* Options */}
      <section className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="prefix-field"
            >
              {strings.prefixAddedToStartOf}
            </label>
            <input
              className="w-full rounded-lg border border-border bg-card px-4 py-2 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="prefix-field"
              onChange={(e) => setPrefix(e.target.value)}
              placeholder={strings.prefixPlaceholder}
              type="text"
              value={prefix}
            />
          </div>
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="suffix-field"
            >
              {strings.suffixAddedToEndOf}
            </label>
            <input
              className="w-full rounded-lg border border-border bg-card px-4 py-2 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="suffix-field"
              onChange={(e) => setSuffix(e.target.value)}
              placeholder={strings.suffixPlaceholder}
              type="text"
              value={suffix}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
            <input
              checked={skipEmpty}
              className="accent-primary"
              onChange={(e) => setSkipEmpty(e.target.checked)}
              type="checkbox"
            />
            {strings.skipEmptyLines}
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
            <input
              checked={numbered}
              className="accent-primary"
              onChange={(e) => setNumbered(e.target.checked)}
              type="checkbox"
            />
            {strings.numberLines123}
          </label>
        </div>

        <div className="space-y-3">
          <p className="font-medium text-foreground text-sm">
            {strings.presets}
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              className="rounded-lg border border-border bg-card px-4 py-2 text-foreground text-sm transition-colors hover:bg-muted"
              onClick={presetQuotes}
              type="button"
            >
              {strings.wrapInDoubleQuotes}
            </button>
            <button
              className="rounded-lg border border-border bg-card px-4 py-2 text-foreground text-sm transition-colors hover:bg-muted"
              onClick={presetCommaSuffix}
              type="button"
            >
              {strings.addCommaSuffix}
            </button>
            <button
              className="rounded-lg border border-border bg-card px-4 py-2 text-foreground text-sm transition-colors hover:bg-muted"
              onClick={presetMarkdownList}
              type="button"
            >
              {strings.markdownList}
            </button>
            <button
              className="rounded-lg border border-border bg-card px-4 py-2 text-foreground text-sm transition-colors hover:bg-muted"
              onClick={presetNumberLines}
              type="button"
            >
              {strings.numberLines}
            </button>
          </div>
        </div>
      </section>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleApply}
          type="button"
        >
          {strings.apply}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="prefix-output"
          >
            {strings.formattedOutput}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="prefix-output"
            readOnly
            value={output}
          />
          <p className="text-muted-foreground text-xs">
            {fmt(outputLineCount)}
            {tPlural(strings.line, locale, outputLineCount)}
          </p>
        </div>
      )}
      {/* Stats */}
      {output.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.inputLines} value={fmt(lineCount)} />
          <StatCard label={strings.outputLines} value={fmt(outputLineCount)} />
          <StatCard
            label={strings.outputCharacters}
            value={fmt(output.length)}
          />
        </div>
      )}
    </>
  );
}
